import type { AnyAction, Middleware, MiddlewareAPI } from 'redux';
import { get } from 'lodash';
import axios from 'axios';
import { types as commonTypes } from '../../actions/backendCommon';
import { objectToCamelCase, objectToSnakeCase, safeStringify } from '../../../utils/background/Utils';
import { captureException } from '../../../tracker/raven';
import { Network } from '../../../errors/Errors';
import { BASE_URL, LOGGER_API_KEY } from '../../../utils/config';
import { ERR_INVALID_AUTH_SCHEME, ERR_REFRESH_FAILED, ERR_LOGIN_FAILED, REQUESTS } from '../../../constants/backend';
import { getAuthToken } from '../../selectors/auth';
import type { RootState } from '../../types';
import type { RequestDescriptor } from '../../actions/backendCommon';

const { ApiError } = Network;

interface ApiResponseLike {
    data?: { error?: unknown } | unknown;
    status?: number;
}

interface ApiRequestError {
    response?: ApiResponseLike;
    request?: unknown;
    message?: string;
    data?: { status?: number };
}

function toApiRequestError(error: unknown): ApiRequestError {
    return (error || {}) as ApiRequestError;
}

function extractApiMessage(response: ApiResponseLike | undefined): unknown {
    const data = response?.data;
    if (typeof data === 'object' && data !== null && 'error' in data) {
        return (data as { error?: unknown }).error;
    }
    return data;
}

function getStatus(error: ApiRequestError): number | undefined {
    const data = error.data;
    return typeof data?.status === 'number' ? data.status : undefined;
}

export const sendRequest = (
    { method, route, apiUrl = BASE_URL }: RequestDescriptor = {},
    jwt: string | null = null,
    data: Record<string, unknown> | null = null,
    queryParams = '',
    keepKeysCase = false
) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
        const configuration: {
            method?: string;
            headers: Record<string, string>;
            data?: unknown;
            params?: unknown;
        } = {
            method,
            headers: { 'Content-Type': 'application/json' }
        };

        if (data) {
            configuration.data = !keepKeysCase ? objectToSnakeCase(data) : data;
        }

        if (queryParams) {
            configuration.params = queryParams;
        }

        if (jwt) {
            configuration.headers.Authorization = `Bearer ${jwt}`;
        }

        if (!jwt && route === REQUESTS.SEND_LOG.route) {
            configuration.headers['X-i2x-API'] = LOGGER_API_KEY;
        }

        try {
            const url = `${apiUrl}${route}`;
            const response = await axios(url, configuration);
            resolve(response.data);
        } catch (error: unknown) {
            const apiError = toApiRequestError(error);
            if (apiError.response) {
                // The request was made and the server responded with a status code that falls out of the range of 2xx
                const message = extractApiMessage(apiError.response);
                const messageText = typeof message === 'string' ? message : String(safeStringify(message));

                reject(
                    new ApiError({
                        method,
                        route,
                        status: apiError.response?.status,
                        message: messageText
                    })
                );
            } else if (apiError.request) {
                // The request was made but no response was received `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                reject(
                    new ApiError({
                        method,
                        route,
                        status: 0,
                        message: `status - none, body - no response received`
                    })
                );
            } else {
                reject(
                    new ApiError({
                        method,
                        route,
                        status: 0,
                        message: apiError.message || 'Unknown network error'
                    })
                );
            }
        }
    });
};

const handleRequest = async (store: MiddlewareAPI, next: (action: unknown) => unknown, action: AnyAction) => {
    try {
        const state = store.getState() as RootState;
        const token = getAuthToken(state) || get(state, 'auth.token', null);
        const response = await sendRequest(
            action.request,
            token,
            get(action, 'payload.body'),
            get(action, 'payload.queryParams'),
            action.keepKeysCase
        );
        if (action.onSuccess) {
            const nextAction = action.onSuccess(objectToCamelCase(response));
            if (nextAction) {
                next(nextAction);
            }
        }
    } catch (error) {
        const typedError = toApiRequestError(error);
        if (
            action.request.route !== REQUESTS.SEND_LOG.route &&
            typedError.message !== ERR_INVALID_AUTH_SCHEME &&
            typedError.message !== ERR_REFRESH_FAILED &&
            typedError.message !== ERR_LOGIN_FAILED
        ) {
            captureException(error);
        }

        const status = getStatus(typedError);
        if (status && [401, 403].includes(status)) {
            next({ type: commonTypes.UNAUTHORIZED_REQUEST, error });
        }

        if (action.onError) {
            const nextAction = action.onError(error);

            if (nextAction) {
                next(nextAction);
            }
        }
    }
};

const api: Middleware = store => next => action => {
    const typedAction = action as AnyAction;
    if (typedAction.type === commonTypes.NETWORK_REQUEST) {
        return handleRequest(store, next, typedAction);
    }

    return next(action);
};

export default api;
